/* Client for the granular Agent Bundle Registry API (api/src/routes/agentBundles.ts).
   The directory loads cheap summaries; the detail view loads one bundle with its
   draft + full version history; edits are partial (draft / version / agent) so we
   no longer rewrite the whole collection on every change. */
import { BASE, safeFetch } from "./client";
import type { CheckMap, CheckMapEntry } from "../types";

/* Directory row — the cheap summary fields, no snapshots loaded. */
export interface BundleSummary {
  id: string;
  name: string;
  note: string | null;
  isArchived: boolean;
  agentCount: number;
  latestVersionLabel: string | null;
  latestVersionAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BundleVersion {
  id: string;
  bundleId: string;
  label: string;
  note: string | null;
  snap: CheckMap;
  createdAt: string;
}

export interface BundleDetail {
  id: string;
  name: string;
  note: string | null;
  isArchived: boolean;
  draft: CheckMap;
  createdAt: string;
  updatedAt: string;
  versions: BundleVersion[];
}

export type DiffStatus = "added" | "removed" | "changed" | "unchanged";
export interface DiffItem {
  agentId: string;
  name: string;
  status: DiffStatus;
  changes: string[];
}
export interface BundleDiff {
  since: string;
  items: DiffItem[];
}

/* GET /agent-bundles — directory list. `archived` filters on the flag; omit for all. */
export async function listBundles(archived?: boolean): Promise<BundleSummary[]> {
  const q = archived === undefined ? "" : `?archived=${archived ? "true" : "false"}`;
  const r = await safeFetch<{ bundles: BundleSummary[] }>(`/agent-bundles${q}`);
  return r?.bundles ?? [];
}

/* GET /agent-bundles/:id — one bundle with draft + version history. */
export async function getBundle(id: string): Promise<BundleDetail | null> {
  const r = await safeFetch<{ bundle: BundleDetail }>(`/agent-bundles/${encodeURIComponent(id)}`);
  return r?.bundle ?? null;
}

export async function createBundle(body: {
  name: string;
  note?: string;
  draft?: CheckMap;
}): Promise<BundleDetail | null> {
  const r = await safeFetch<{ bundle: BundleDetail }>("/agent-bundles", {
    method: "POST",
    body: JSON.stringify(body),
  });
  return r?.bundle ?? null;
}

/* Rename, edit note, or archive/unarchive. */
export async function updateBundle(
  id: string,
  patch: { name?: string; note?: string | null; isArchived?: boolean },
): Promise<boolean> {
  const r = await safeFetch(`/agent-bundles/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
  return r != null;
}

/* Snapshot the current draft as a new immutable version. */
export async function saveVersion(id: string, note?: string): Promise<BundleVersion | null> {
  const r = await safeFetch<{ version: BundleVersion }>(
    `/agent-bundles/${encodeURIComponent(id)}/versions`,
    { method: "POST", body: JSON.stringify({ note }) },
  );
  return r?.version ?? null;
}

/* Replace the entire draft AgentMap (used by inline draft edits + add/remove). */
export async function replaceDraft(id: string, draft: CheckMap): Promise<boolean> {
  const r = await safeFetch(`/agent-bundles/${encodeURIComponent(id)}/draft`, {
    method: "PUT",
    body: JSON.stringify(draft),
  });
  return r != null;
}

/* Reset the draft to a saved version's snapshot (the version is untouched). */
export async function revertDraft(id: string, versionId: string): Promise<CheckMap | null> {
  const r = await safeFetch<{ draft: CheckMap }>(
    `/agent-bundles/${encodeURIComponent(id)}/draft/revert`,
    { method: "POST", body: JSON.stringify({ versionId }) },
  );
  return r?.draft ?? null;
}

/* Add or replace one agent in the draft (manual config path). */
export async function addAgent(
  id: string,
  agentId: string,
  config: CheckMapEntry,
): Promise<CheckMap | null> {
  const r = await safeFetch<{ draft: CheckMap }>(
    `/agent-bundles/${encodeURIComponent(id)}/agents`,
    { method: "POST", body: JSON.stringify({ agentId, config }) },
  );
  return r?.draft ?? null;
}

export async function deleteAgent(id: string, agentId: string): Promise<CheckMap | null> {
  const r = await safeFetch<{ draft: CheckMap }>(
    `/agent-bundles/${encodeURIComponent(id)}/agents/${encodeURIComponent(agentId)}`,
    { method: "DELETE" },
  );
  return r?.draft ?? null;
}

/* Fork a bundle into a new one, seeded from a version's snap (or the draft). */
export async function duplicateBundle(
  id: string,
  name: string,
  fromVersionId?: string,
): Promise<BundleDetail | null> {
  const r = await safeFetch<{ bundle: BundleDetail }>(
    `/agent-bundles/${encodeURIComponent(id)}/duplicate`,
    { method: "POST", body: JSON.stringify({ name, fromVersionId }) },
  );
  return r?.bundle ?? null;
}

/* Structured draft-vs-version diff used to pre-fill the save-version note. */
export async function diffDraft(id: string, versionId: string): Promise<BundleDiff | null> {
  return safeFetch<BundleDiff>(`/agent-bundles/${encodeURIComponent(id)}/diff`, {
    method: "POST",
    body: JSON.stringify({ versionId }),
  });
}

/* Natural-language → AgentConfig generation (server-side OpenRouter call using
   the model + prompt from the Settings page). Returns the config for review; the
   user confirms before it is added to a draft. */
export async function generateAgent(
  description: string,
  kind: "spine" | "workstream",
): Promise<CheckMapEntry | null> {
  const r = await safeFetch<{ config: CheckMapEntry }>("/checks/generate-agent", {
    method: "POST",
    body: JSON.stringify({ description, kind }),
  });
  return r?.config ?? null;
}

/* Flattened, newest-first list of every saved version across all bundles —
   the source for the single simulation-setup dropdown (PR7). Drafts excluded. */
export interface FlatVersionOption {
  bundleId: string;
  bundleName: string;
  versionId: string;
  label: string;
  agentCount: number;
  createdAt: string;
}

export function bundleVersionUrl(id: string): string {
  return `${BASE}/agent-bundles/${encodeURIComponent(id)}`;
}
